import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, BookOpen, Clock } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { BackToTop } from "@/components/BackToTop";
import { ArticleCTA } from "@/components/ArticleCTA";
import { seo } from "@/lib/seo";
import { getAllPosts } from "@/lib/aprenda";
import { AprendaIndexSkeleton } from "@/components/skeletons";

export const Route = createFileRoute("/aprenda/")({
  head: () =>
    seo({
      path: "/aprenda",
      title: "Guias de Finanças Pessoais — Seu Bolso Tranquilo",
      description:
        "Como fazer controle financeiro pessoal na prática: guias gratuitos sobre organizar finanças sem planilha, método 50/30/20, sair das dívidas e guardar dinheiro.",
      breadcrumbs: [
        { name: "Início", path: "/" },
        { name: "Aprenda", path: "/aprenda" },
      ],
    }),
  component: AprendaIndex,
  pendingComponent: AprendaIndexSkeleton,
});

function AprendaIndex() {
  const posts = getAllPosts();

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-background/70 border-b border-border/40">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <BrandLogo className="size-8" alt="Logo Seu Bolso Tranquilo" />
            <span className="font-semibold">Seu Bolso Tranquilo</span>
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm text-muted-foreground">
            <Link to="/" className="hover:text-foreground transition-colors">Início</Link>
            <Link to="/aprenda" className="text-foreground font-medium">Aprenda</Link>
            <Link to="/contato" className="hover:text-foreground transition-colors">Contato</Link>
          </nav>
          <Link
            to="/auth"
            className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            Começar grátis
            <ArrowRight className="size-4" />
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-16 sm:py-24">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-primary mb-4">
            <BookOpen className="size-4" />
            Aprenda
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-5">
            Guias práticos pra organizar o dinheiro
          </h1>
          <p className="text-lg text-muted-foreground leading-relaxed">
            Conteúdo em português, sem jargão, baseado no que funciona pra pessoas reais. Comece pelo que mais te interessa.
          </p>
        </div>

        <section className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" aria-label="Lista de artigos">
          {posts.map((p) => (
            <Link
              key={p.slug}
              to="/aprenda/$slug"
              params={{ slug: p.slug }}
              className="group flex flex-col rounded-3xl border border-border/60 bg-card p-6 transition-all hover:border-primary/40 hover:shadow-lg"
            >
              <div className="flex items-center justify-between text-xs text-muted-foreground mb-4">
                <span className="rounded-full bg-primary/10 text-primary px-3 py-1 font-medium">
                  {p.category}
                </span>
                <span className="inline-flex items-center gap-1">
                  <Clock className="size-3.5" />
                  {p.readingMinutes} min
                </span>
              </div>
              <h2 className="text-xl font-bold mb-3 leading-snug group-hover:text-primary transition-colors">
                {p.title}
              </h2>
              <p className="text-sm text-muted-foreground leading-relaxed flex-1">
                {p.description}
              </p>
              <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-primary">
                Ler artigo
                <ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" />
              </span>
            </Link>
          ))}
        </section>

        <div className="mt-16">
          <ArticleCTA variant="final" />
        </div>
      </main>

      <BackToTop />
    </div>
  );
}